import { useEffect, useState } from 'react';

/**
 * Custom hook to check if user prefers reduced motion
 * Respects WCAG 2.3.3 - Animation from Interactions
 * Returns true if user prefers reduced motion
 */
export const useReducedMotion = (): boolean => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    // Check if matchMedia is supported
    if (typeof window === 'undefined' || !window.matchMedia) {
      return;
    }

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    
    // Set initial value
    setPrefersReducedMotion(mediaQuery.matches);

    // Listen for changes
    const handleChange = (event: MediaQueryListEvent) => {
      setPrefersReducedMotion(event.matches);
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } 
    // Legacy browsers
    else if (mediaQuery.addListener) {
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, []);

  return prefersReducedMotion;
};

/**
 * Helper function to get animation duration based on user preference
 * @param duration - Duration in seconds (default: 0.6)
 * @returns Duration adjusted for reduced motion preference
 */
export const getAnimationDuration = (duration: number = 0.6): number => {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return duration;
  }
  
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  return prefersReducedMotion ? 0 : duration;
};

/**
 * Helper function to get animation variants based on user preference
 * @param variants - Framer Motion variants object
 * @param reducedVariants - Alternative variants for reduced motion (optional)
 * @returns Variants adjusted for reduced motion preference
 */
export const getMotionVariants = (
  variants: any,
  reducedVariants?: any
): any => {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return variants;
  }
  
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  
  if (prefersReducedMotion && reducedVariants) {
    return reducedVariants;
  }
  
  if (prefersReducedMotion) {
    // Remove animations from variants
    return Object.keys(variants).reduce((acc, key) => {
      acc[key] = {
        ...variants[key],
        transition: { duration: 0 }
      };
      return acc;
    }, {} as any);
  }
  
  return variants;
};
